import { Button, Divider, Empty, Input, Row, Tree } from 'antd';
import React, { useCallback, useContext, useState } from 'react';

import { FRONT_WARNING, PREFIX } from '@app/constants';
import { useWindowSize } from '@app/hooks';
import {
  CHF_SERVICE_IDS,
  CHF_VISITS_FILTER,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext, NotificationContext } from '@app/providers';

import {
  getFiltersArray,
  hideFilterBlock,
} from '@pages/Clients/ClientsList/utils';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';

import type { DataNode } from 'antd/es/tree';
import type { ChangeEvent, Key } from 'react';

import '@pages/Clients/ClientsList/styles/Filters.desktop.scss';

const { Search } = Input;

export const VisitsServices = ({
  filterGroupIndex,
}: {
  filterGroupIndex: number;
}) => {
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const {
    filters,
    allCategoriesAndServices,
    searchedCategoriesAndServices,
    setFilters,
    setSearchedCategoriesAndServices,
    handleDisabling,
    setUpServerDataChunk,
  } = useContext(ClientFiltersContext);
  const { showNotification } = useContext(NotificationContext);

  const { innerWidth } = useWindowSize();

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.target?.value.toLowerCase() || '';

    setSearchedCategoriesAndServices(
      allCategoriesAndServices.filter(({ children, ...item }: DataNode) => {
        const categoryName = 'Name' in item ? item?.Name : item?.title;
        const servicesNames = (children || [])?.map((service) => {
          return 'Name' in service && typeof service?.Name === 'string'
            ? service?.Name
            : typeof service?.title === 'string'
            ? service?.title
            : '';
        });

        const isSearchedIntoCategories = String(categoryName || '')
          .toLowerCase()
          .includes(searchQuery);

        const isSearchedIntoServices = servicesNames.find((title) =>
          String(title).toLowerCase().includes(searchQuery)
        );

        if (isSearchedIntoCategories || isSearchedIntoServices) {
          return true;
        }
      })
    );
  };

  const onExpand = (newExpandedKeys: React.Key[]) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onCheck = useCallback(
    (checked: Key[] | { checked: Key[]; halfChecked: Key[] }): void => {
      if (!Array.isArray(checked))
        return showNotification(
          'При извлечении выбранных значений произошла ошибка',
          FRONT_WARNING
        );

      const checkedServiceIds = checked
        .map((item) => `${item}`)
        .filter((item) => !item.includes('parent-'))
        .map((item) => Number(item.split('-')[1]));

      const filtersByVisits: NClientsList.Filters['VisitsFilter'] =
        getFiltersArray<NClientsList.Filters['VisitsFilter']>({
          ...(filters.VisitsFilter.filter(
            (item, index) => index !== filterGroupIndex
          ) || []),
          [filterGroupIndex]: {
            ...filters.VisitsFilter[filterGroupIndex],
            Values: [
              {
                ...filters.VisitsFilter?.[filterGroupIndex]?.Values?.[0],
                ServiceIds: checkedServiceIds,
              },
            ],
          },
        });

      const result = {
        ...filters,
        VisitsFilter: filtersByVisits,
      };

      setFilters(result);
    },
    [filters, filterGroupIndex, showNotification, setFilters]
  );

  return (
    <div
      className={`${PREFIX}chf-visits-services`}
      onClick={(e: MouseEvent | React.MouseEvent) => e.stopPropagation()}
    >
      <Search
        className="search-field"
        placeholder="Поиск по услугам"
        onChange={onChange}
      />
      {searchedCategoriesAndServices.length > 0 ? (
        <Tree
          className="items-tree"
          checkable
          onExpand={onExpand}
          onCheck={onCheck}
          checkedKeys={
            filters.VisitsFilter?.[
              filterGroupIndex
            ]?.Values?.[0]?.ServiceIds?.map((item) => `child-${item}`) || []
          }
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          treeData={searchedCategoriesAndServices}
        />
      ) : (
        <Empty />
      )}
      <Divider className="button-divider" />
      <Row justify="end">
        <Button
          type="primary"
          size="small"
          disabled={handleDisabling(
            CHF_VISITS_FILTER,
            CHF_SERVICE_IDS,
            false,
            '',
            filterGroupIndex
          )}
          onClick={() => {
            setUpServerDataChunk(
              CHF_VISITS_FILTER,
              CHF_SERVICE_IDS,
              false,
              '',
              filterGroupIndex
            );

            hideFilterBlock(
              '.visits-services-label',
              innerWidth,
              filterGroupIndex
            );
          }}
        >
          Выбрать
        </Button>
      </Row>
    </div>
  );
};
